import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";

export default function TermsAndConditions() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-pink-600 via-purple-600 to-pink-600 text-white py-6">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            Terms & Conditions
          </h1>
          <p className="text-white/80 mt-2">Last updated: {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</p>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose prose-pink max-w-none">
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              By enrolling in any course or program offered by MUAS Academy (Makeup Artist Academy), located at C-127, Harmu Housing Colony, Harmu Ranchi 834002, Jharkhand, India, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not enroll in our courses or use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Course Enrollment Process and Eligibility</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              To enroll in MUAS Academy courses, applicants must:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Be at least 16 years of age (minors require parental/guardian consent)</li>
              <li>Complete the enrollment form with accurate personal information</li>
              <li>Pay the required registration fee and course fees as per the selected program</li>
              <li>Submit valid identification documents as requested</li>
              <li>Attend the scheduled orientation session (if applicable)</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              MUAS Academy reserves the right to refuse admission at its sole discretion without providing reasons.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">3. Fees and Payment Terms</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>All course fees must be paid in full before the commencement of the course, unless an installment plan has been agreed upon in writing.</li>
              <li>Registration fees are non-refundable under any circumstances.</li>
              <li>Course fees include training materials as specified in the course brochure. Additional kits or products may be charged separately.</li>
              <li>Payment can be made via cash, bank transfer, UPI, or other approved payment methods.</li>
              <li>Failure to pay fees on time may result in suspension from classes until dues are cleared.</li>
              <li>GST and other applicable taxes will be charged as per government regulations.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">4. Attendance and Conduct Policy</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Students enrolled at MUAS Academy are expected to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Maintain a minimum attendance of 80% to be eligible for certification</li>
              <li>Arrive on time for all scheduled classes and practical sessions</li>
              <li>Inform the academy in advance in case of absence due to genuine reasons</li>
              <li>Maintain professional conduct and respect towards instructors, staff, and fellow students</li>
              <li>Follow all safety guidelines during practical sessions</li>
              <li>Keep the training area clean and organized</li>
              <li>Not engage in any form of harassment, discrimination, or disruptive behavior</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              MUAS Academy reserves the right to dismiss students who violate conduct policies without refund.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Certification Issuance</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Certificates will be issued upon successful completion of the course, subject to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Meeting the minimum attendance requirement (80%)</li>
              <li>Successful completion of all practical assessments and assignments</li>
              <li>Clearing any pending fees or dues</li>
              <li>Submission of all required documents</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              Certificates are typically issued within 15-30 days after course completion. The academy's decision on certification is final.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Intellectual Property Rights</h2>
            <p className="text-gray-600 leading-relaxed">
              All course materials, including but not limited to training manuals, videos, presentations, techniques, and proprietary methods taught at MUAS Academy, are the intellectual property of MUAS Academy. Students agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-4">
              <li>Copy, reproduce, or distribute course materials without written permission</li>
              <li>Record classes or practical sessions without prior approval</li>
              <li>Share proprietary techniques or trade secrets with third parties</li>
              <li>Use the MUAS Academy name or logo for personal commercial purposes without authorization</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              MUAS Academy shall not be held liable for:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-4">
              <li>Any personal injury occurring during training sessions, except where caused by gross negligence on the part of the academy</li>
              <li>Loss or damage to personal belongings brought to the premises</li>
              <li>Career outcomes or employment after course completion</li>
              <li>Any allergic reactions to products used during training (students must disclose known allergies beforehand)</li>
              <li>Circumstances beyond our control including natural disasters, pandemics, or government-mandated closures</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Modification of Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              MUAS Academy reserves the right to modify these Terms and Conditions at any time. Changes will be communicated via our website or direct notification. Continued enrollment after such changes constitutes acceptance of the modified terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Governing Law and Jurisdiction</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms and Conditions shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or relating to these terms shall be subject to the exclusive jurisdiction of the courts in Ranchi, Jharkhand, India.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">10. Contact Information</h2>
            <p className="text-gray-600 leading-relaxed">
              For any queries regarding these Terms and Conditions, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="text-gray-700"><strong>MUAS Academy</strong></p>
              <p className="text-gray-600">C-127, Harmu Housing Colony, Harmu Ranchi 834002</p>
              <p className="text-gray-600">Jharkhand, India</p>
              <p className="text-gray-600">Phone: +91 6201542274, +91 9773682082</p>
              <p className="text-gray-600">Email: muasacademy.ranchi@gmail.com</p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
